import { useContext, useEffect, useState } from 'react'
import textServices from 'services/text-services'
import ButtonCloseMenu from 'components/button-close'
import useRouter from 'hooks/use-router'
import useMainClass from 'hooks/use-main-cass'
import { LoaderContext } from 'contexts/loader-context'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { F1OnboardingSlide, F1RulesContent } from '../constants'
import F1RulesSheet from './F1RulesSheet'

interface F1DynamicOnboardingProps {
  slides: F1OnboardingSlide[]
  /** Section label for tracking, e.g. "trivia :: 22" */
  trackSection: string
  onFinish: () => void
  /** Skip button copy (ticket/photo frames say "Omitir instrucciones") */
  skipLabel?: string
  /** When present, renders the "VER LAS REGLAS" link + rules sheet */
  rules?: F1RulesContent
}

// Campaign onboarding carousel (Figma 5558:3154 / 5558:2956): dark screen
// with "¿CÓMO PARTICIPAR?" header copy, one illustrated slide per step,
// dot navigation with prev/next arrows and a skip button. Reused by the
// trivia and both rally dynamics.
const F1DynamicOnboarding = ({ slides, trackSection, onFinish, skipLabel, rules }: F1DynamicOnboardingProps) => {
  useMainClass('f1-onboarding')
  const { history } = useRouter()
  const { setFooter } = useContext(LoaderContext)
  const [index, setIndex] = useState(0)
  const [showRules, setShowRules] = useState(false)

  // Full-screen step: hide the global footer while mounted (same pattern
  // as the age gate), restoring it on unmount.
  useEffect(() => {
    setFooter(false)
    return () => setFooter(true)
  }, [])

  const isLast = index === slides.length - 1
  const slide = slides[index]

  const goNext = () => {
    if (isLast) {
      onFinish()
      return
    }
    setIndex(index + 1)
  }

  const skip = () => {
    dlTrackEvent('user_interaction', {
      action: 'click',
      element: 'onboarding :: omitir introduccion',
      section: trackSection
    })
    onFinish()
  }

  return (
    <div className="f1-onboarding__screen">
      <ButtonCloseMenu open={true} paramFn={() => history.goBack()} />
      <div className="f1-onboarding__header">
        <h1 className="f1-onboarding__title">
          {textServices.findByText('pitstops/onboarding/title', '¿CÓMO PARTICIPAR?')}
        </h1>
        <p className="f1-onboarding__subtitle">
          {textServices.findByText('pitstops/onboarding/subtitle', 'COMPLETA EL RETO Y GANA')}
        </p>
      </div>
      <div className="f1-onboarding__slide">
        <img className="f1-onboarding__art" src={slide.image} alt="" aria-hidden="true" />
        <h2 className="f1-onboarding__slide-title">{slide.title}</h2>
        <p className="f1-onboarding__slide-text">{slide.text}</p>
        <div className="f1-onboarding__nav">
          <button
            type="button"
            className="f1-onboarding__arrow"
            aria-label={textServices.findByText('pitstops/onboarding/prev', 'Anterior') as string}
            disabled={index === 0}
            onClick={() => setIndex(Math.max(0, index - 1))}
          >
            ‹
          </button>
          <div className="f1-onboarding__dots">
            {slides.map((item, dot) => (
              <span
                key={item.title}
                className={`f1-onboarding__dot${dot === index ? ' f1-onboarding__dot--active' : ''}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="f1-onboarding__arrow"
            aria-label={textServices.findByText('pitstops/onboarding/next', 'Siguiente') as string}
            onClick={goNext}
          >
            ›
          </button>
        </div>
      </div>
      <button type="button" className="f1-onboarding__skip" onClick={skip}>
        {skipLabel ?? (textServices.findByText('pitstops/onboarding/skip', 'Omitir introducción') as string)}
      </button>
      {rules && (
        <button
          type="button"
          className="f1-onboarding__rules-link"
          onClick={() => {
            dlTrackEvent('user_interaction', {
              action: 'click',
              element: 'onboarding :: ver las reglas',
              section: trackSection
            })
            setShowRules(true)
          }}
        >
          {textServices.findByText('pitstops/onboarding/rules', 'VER LAS REGLAS')}
        </button>
      )}
      {rules && showRules && <F1RulesSheet rules={rules} onClose={() => setShowRules(false)} />}
    </div>
  )
}

export default F1DynamicOnboarding
